import { getStyleSchema, type GameType } from '@low6-pickem/style-config';
import { useFormikContext } from 'formik';
import { SchemaDrivenStylingForm } from '../styling/SchemaDrivenStylingForm';
import { useUpdatePickemMutation } from '@/api/pickems/updatePickem';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Star, StarOff, Info } from 'lucide-react';

interface PickemStylingTabProps {
  pickemId: string;
  isDefaultStyle?: boolean;
  gameType?: GameType;
  titleEn?: string;
  tenantId?: string;
  tenantCode?: string;
  disabled?: boolean;
}

export function PickemStylingTab({
  pickemId,
  isDefaultStyle = false,
  gameType = 'pickem',
  titleEn = '',
  tenantId,
  tenantCode,
  disabled,
}: PickemStylingTabProps) {
  const schema = getStyleSchema(gameType);
  const { mutate: updatePickem, isPending } = useUpdatePickemMutation({ redirect: false });
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const styleConfig = (values.style_config as Record<string, unknown> | undefined) ?? {};

  const handleSetDefault = () => {
    updatePickem({
      id: pickemId,
      tenant_id: tenantId ?? '',
      title_en: titleEn,
      is_default_style: true,
    } as any);
  };

  const handleRemoveDefault = () => {
    updatePickem({
      id: pickemId,
      tenant_id: tenantId ?? '',
      title_en: titleEn,
      is_default_style: false,
    } as any);
  };

  if (!schema) return null;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-md border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2">
            {isDefaultStyle ? (
              <Badge variant="secondary" className="gap-1">
                <Star className="h-3 w-3 fill-current" />
                Default style template
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">
                This style will auto-apply to new {gameType} contests on this tenant when set as
                default.
              </span>
            )}
          </div>
          {isDefaultStyle ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveDefault}
                  disabled={isPending || disabled}
                  className="gap-1"
                >
                  <StarOff className="h-4 w-4" />
                  Remove as default
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Stop using this pickem's style as the template for new {gameType} contests
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSetDefault}
                  disabled={isPending || disabled}
                  className="gap-1"
                >
                  <Star className="h-4 w-4" />
                  Set as default
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Use this pickem's style config as the starting template for new {gameType} contests
                on this tenant
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Image options — always toggleable, independent of contest editability */}
        <div className="flex flex-col gap-3 rounded-md border p-4">
          <p className="text-sm font-medium">Image options</p>
          <div className="flex items-center gap-2">
            <Switch
              id="style_config.lowResImages"
              checked={styleConfig.lowResImages === true}
              onCheckedChange={checked =>
                setFieldValue('style_config', { ...styleConfig, lowResImages: checked })
              }
            />
            <div className="flex items-center gap-1">
              <Label htmlFor="style_config.lowResImages" className="cursor-pointer">
                Use low-resolution assets
              </Label>
              <Tooltip>
                <TooltipTrigger type="button">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  When on, the player frontend loads team and jersey artwork from the
                  lower-resolution <code>-low</code> blob folders (e.g. <code>mlb/teams-low</code>).
                  Admin previews are unaffected.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        <SchemaDrivenStylingForm
          schema={schema}
          fieldName="style_config"
          contestId={pickemId}
          disabled={disabled}
          tenantCode={tenantCode}
        />
      </div>
    </TooltipProvider>
  );
}
